import colors from '../colors';
import { darkenColor } from '../colorUtils';
import { rawColors } from './rawColors';
import t from '../../i18n/t';

/**
 * A soft tint behind the app instead of pure white (Petr, 2026-10-10); dark mode has its own, below. Each one comes as the
 * three shades the light theme layers: `background` for cards and popups, `backgroundLight` a touch deeper, and
 * `backgroundDark` for the panels behind them, as white, #F9FAFC and whitesmoke are by default.
 */
export type SoftBackground = {
  /** What the account keeps; short, as the settings have a size limit. */
  key: string;
  label: string;
  background: string;
  backgroundLight: string;
  backgroundDark: string;
  /** Dark ones only: the three-dots menus, a shade apart from the cards. */
  menu?: string;
};

export const softBackgrounds: SoftBackground[] = [
  // An off-white with only a whisper of warmth, like Claude's own window, #FCFCFB with #F0F0EF bubbles (Petr,
  // 2026-10-10; a first try, #FDFCFA on #F5F4EE, was a little too orange). The panels sit a step below, as whitesmoke
  // does below white. First, and the default since 2026-10-11: accounts on White moved to it once (`softWhiteSwitch`).
  {
    key: 'ivory',
    label: t.BACKGROUND_SOFT_WHITE,
    background: '#FCFCFB',
    backgroundLight: '#F8F8F6',
    backgroundDark: '#F3F3F1'
  },
  {
    key: 'white',
    label: t.BACKGROUND_WHITE,
    background: colors.white,
    backgroundLight: colors.backgroundLight,
    backgroundDark: colors.whitesmoke
  },
  // A clear, airy pale blue, the light partner to Midnight and Navy (Petr, 2026-10-11). Blue, not blue-grey: the first
  // Mist, a cool blue-grey, felt harsh. Briefly the default on 2026-10-11, until its blue undertone proved harsher on
  // the eyes than Soft white (Petr).
  // Warmed with a touch of Lavender, toward periwinkle (Petr, 2026-10-11: #F7FAFE / #F1F6FD / #EAF2FC was icy).
  { key: 'sky', label: t.BACKGROUND_SKY, background: '#F8F9FE', backgroundLight: '#F2F3FD', backgroundDark: '#ECEDFB' },
  { key: 'cream', label: t.BACKGROUND_CREAM, background: '#FCFAF5', backgroundLight: '#F8F5EE', backgroundDark: '#F3EFE6' },
  // A soft, faintly warm grey (Petr, 2026-10-10: the first, cool blue-grey felt too harsh).
  { key: 'mist', label: t.BACKGROUND_MIST, background: '#FAFAF9', backgroundLight: '#F5F5F3', backgroundDark: '#EFEFEC' },
  // Less green, nearer the warm grey (Petr, 2026-10-10: the first felt too harsh).
  { key: 'sage', label: t.BACKGROUND_SAGE, background: '#F9FAF7', backgroundLight: '#F4F6F1', backgroundDark: '#EEF1EA' },
  // Blush softened with Sage (Petr, 2026-10-10): just a hint of colour over the warm grey.
  { key: 'blush', label: t.BACKGROUND_BLUSH, background: '#FBF8F7', backgroundLight: '#F7F3F2', backgroundDark: '#F2EDEB' }
];

/**
 * The dark mode's own backgrounds (Petr, 2026-10-10), picked apart from the light one so Auto can go from Cream by day
 * to Midnight at night. In dark the panels sit below the cards, so `backgroundLight` is the panel shade too, as in the
 * dark theme. Slate is the dark mode as it was before; Ink, first, is the default since 2026-10-11 (Petr).
 */
export const darkBackgrounds: SoftBackground[] = [
  // Close to neutral, as the Claude app's own dark window (Petr, 2026-10-11, from a screenshot: main #151515, sidebar
  // #111111, bubbles and the text box #1D1D1B–#1F1F1F, selected row #343434, lines #292929). The main surfaces take the
  // main area's shade and the panels the sidebar's; the menus the raised bubbles'. (The first try gave the main surfaces
  // the bubbles' shade, which read flipped, Petr.)
  {
    key: 'ink',
    label: t.BACKGROUND_INK,
    background: '#151515',
    backgroundLight: '#111111',
    backgroundDark: '#111111',
    menu: '#1F1F1E'
  },
  // Near-black with a faint cool tint over pure black panels, as Reddit's dark mode (Petr, 2026-10-11, from a
  // screenshot: page #0F1112, side panel #000000, raised controls #2C3237, lines about #2C2E2F). The menus are raised a
  // shade, as there is nothing darker than the panels to sink them into.
  {
    key: 'onyx',
    label: t.BACKGROUND_ONYX,
    background: '#0F1112',
    backgroundLight: '#000000',
    backgroundDark: '#000000',
    menu: '#1A1D1F'
  },
  // Slate-navy cards over a navy-black, as a browser's dark mode Petr liked (2026-10-11, after Onyx; from a screenshot: panels and
  // address bar #2B334B, main page #020715, selected tab #465078). Shovel's panels sit below its cards, so the cards take
  // the lighter navy and the near-black goes behind them. A high-contrast cousin of Midnight.
  {
    key: 'navy',
    label: t.BACKGROUND_NAVY,
    background: '#2B334B',
    backgroundLight: '#020715',
    backgroundDark: '#020715',
    menu: '#1C2236'
  },
  {
    key: 'slate',
    label: t.BACKGROUND_SLATE,
    background: rawColors.gray2,
    backgroundLight: darkenColor(rawColors.gray2, -15),
    backgroundDark: darkenColor(rawColors.gray2, -15),
    menu: '#20272D'
  },
  {
    key: 'midnight',
    label: t.BACKGROUND_MIDNIGHT,
    background: '#252A37',
    backgroundLight: '#1E222D',
    backgroundDark: '#1E222D',
    menu: '#1A1D27'
  },
  {
    key: 'charcoal',
    label: t.BACKGROUND_CHARCOAL,
    background: '#2B2B2B',
    backgroundLight: '#232323',
    backgroundDark: '#232323',
    menu: '#1E1E1E'
  },
  // A warm dark grey, only a hint of brown (Petr, 2026-10-10: the first was too brown).
  {
    key: 'espresso',
    label: t.BACKGROUND_ESPRESSO,
    background: '#2D2B29',
    backgroundLight: '#252321',
    backgroundDark: '#252321',
    menu: '#201F1D'
  }
];

/** The account's dark background, Ink when it never picked one or holds one this version doesn't know. */
export const darkBackground = (key?: string | null): SoftBackground =>
  darkBackgrounds.find(item => item.key === key) ?? darkBackgrounds[0];

/** `color` moved `amount` (0–1) of the way toward `toward`, as a hex, so code can still add an alpha to it. */
export const mixHex = (color: string, toward: string, amount: number) => {
  const channels = (hex: string) => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  const from = channels(color);
  const to = channels(toward);
  return `#${from
    .map((value, i) =>
      Math.round(value + (to[i] - value) * amount)
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`.toUpperCase();
};

/** The account's background, Soft white when it never picked one or holds one this version doesn't know. */
export const softBackground = (key?: string | null): SoftBackground =>
  softBackgrounds.find(item => item.key === key) ?? softBackgrounds[0];
